/**
 * Fractional positions for drag-and-drop ordering.
 *
 * A card dropped between two others takes a position midway between its new
 * neighbours, so a reorder writes ONE row instead of renumbering the column.
 *
 * The catch is that midpoints cannot be taken forever: doubles run out of
 * representable values between two adjacent numbers after roughly fifty
 * insertions in the same gap. That is not hypothetical for a column someone
 * drags into repeatedly, so exhaustion is detected explicitly and answered by
 * rebalancing the column rather than silently writing a duplicate position.
 */

/** Spacing used when appending to either end of a column. */
const STEP = 1;

/**
 * Position for a card inserted between two neighbours.
 *
 * Pass null for a missing neighbour (top of column, bottom of column, or an
 * empty column). Returns null when the gap can no longer be subdivided, which
 * means the caller must rebalance.
 */
export function computePosition(before: number | null, after: number | null): number | null {
  if (before === null && after === null) return 0;
  if (before === null) return after! - STEP;
  if (after === null) return before + STEP;

  // Neighbours out of order means the caller mixed up its indices; treating
  // this as exhaustion forces a rebalance, which repairs it either way.
  if (before >= after) return null;

  const mid = before + (after - before) / 2;

  // If the midpoint is not strictly between the two, precision is gone.
  return mid > before && mid < after ? mid : null;
}

/** Evenly spaced positions for a whole column, used after exhaustion. */
export function rebalancePositions(count: number): number[] {
  return Array.from({ length: count }, (_, i) => (i + 1) * STEP);
}

export interface OrderedItem {
  id: string;
  position: number;
}

export interface Move {
  id: string;
  position: number;
}

/**
 * Works out the writes needed to place `activeId` at `toIndex` of `column`.
 *
 * Returns every row that must change — normally exactly one, or the whole
 * column when a rebalance was required. `column` must be the destination's
 * current contents in display order, EXCLUDING the dragged card.
 */
export function planMove(
  activeId: string,
  column: OrderedItem[],
  toIndex: number,
): Move[] {
  const clamped = Math.max(0, Math.min(toIndex, column.length));
  const before = clamped > 0 ? (column[clamped - 1]?.position ?? null) : null;
  const after = clamped < column.length ? (column[clamped]?.position ?? null) : null;

  const position = computePosition(before, after);
  if (position !== null) return [{ id: activeId, position }];

  // Out of room: rewrite the whole column with fresh spacing, the dragged card
  // included at its new index.
  const ids = column.map((item) => item.id);
  ids.splice(clamped, 0, activeId);
  const positions = rebalancePositions(ids.length);

  return ids.map((id, i) => ({ id, position: positions[i]! }));
}
